import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import React, {useMemo} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pathToURL} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {makeReactContext} from 'lib/makeReactContext';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import SetStudyPhaseDropdown from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Breadcrumbs, {CrumbLink} from 'ui-library/Breadcrumbs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {CenteredSpinner} from 'ui-library/Spinner';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {StudyOverviewTabContentsProps} from '../utils/TypedStudyTypes';
import AddProcedureRunButton from '../utils/ui/AddProcedureRunButton';
import AnalyzeMultipleProcedureRunsButton from '../utils/ui/AnalyzeMultipleProcedureRunsButton';
import AnalyzeProcedureRunButton from '../utils/ui/AnalyzeProcedureRunButton';
import LinkToExistingUnitOperationsButton from '../utils/ui/LinkToExistingUnitOperationsButton';
import {
  MultiProcedureRunOnlyOverviewQuery,
  MultiProcedureRunOnlyOverviewQueryVariables,
} from './__generated/MultiProcedureRunOnlyOverviewQuery';
import {
  MultiProcedureRunPlanOverviewQuery,
  MultiProcedureRunPlanOverviewQueryVariables,
} from './__generated/MultiProcedureRunPlanOverviewQuery';
import {getProcedureRunPlanName} from './ProcedureRunPlanUtils';
import StudyExecutionHeader, {StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';

export enum ProcedureRunTab {
  OVERVIEW = 'overview',
  DESIGN = 'design',
  METADATA = 'metadata',
}

export interface ProcedureRunOrPlanStateSummary {
  id: string | null;
  name: string | null;
}

export const StudyOverviewContext = makeReactContext<{
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
}>(
  () => ({
    selectedProcedureRun: {id: null, name: null},
    selectedProcedureRunPlan: {id: null, name: null},
    currentProcedureRunTab: ProcedureRunTab.OVERVIEW,
    setCurrentProcedureRunTab: noop,
  }),
  'StudyOverviewContext'
);

/*
 * Exported for testing
 */
export function studyOverviewCrumbs(props: {
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  studyName: string;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  studyId: string;
}): JSX.Element {
  return (
    <>
      <div {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'breadcrumbs'})}>
        <Breadcrumbs>
          <CrumbLink
            onClick={() => {
              // resets the procedure run tab to overview when selecting a new procedure run or design when selecting a new procedure run plan
              props.setCurrentProcedureRunTab(
                props.selectedProcedureRunPlan.id != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW
              );
              navigateToStudyOverviewTab(props.studyId);
            }}
          >
            {props.studyName}
          </CrumbLink>
        </Breadcrumbs>
      </div>
      {props.selectedProcedureRun.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRun.name}</div>
      )}
      {props.selectedProcedureRunPlan.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRunPlan.name}</div>
      )}
    </>
  );
}

/*
 * Exported for testing
 */
export function procedureRunViewerTabsProps({
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
  selectedProcedureRunId,
  selectedProcedureRunPlanId,
}: {
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  selectedProcedureRunId: string | null;
  selectedProcedureRunPlanId: string | null;
}): StudyExecutionHeaderTabsProps<ProcedureRunTab> {
  const headerTabContainerProps = {
    activeTab: currentProcedureRunTab,
    className: css(styles.tabContainer),
    headerDividerClassName: css(styles.headerDivider),
    isFullHeight: true,
    navClassName: css(styles.tabList),
    onRequestTabChange: setCurrentProcedureRunTab,
    paneClassName: css(styles.tabPanel),
    tabClassName: css(styles.tab),
  };
  const headerTabsProps = [
    {tabKey: ProcedureRunTab.DESIGN, title: 'DESIGN'},
    {tabKey: ProcedureRunTab.METADATA, title: 'METADATA'},
  ];
  if (selectedProcedureRunId != null) {
    // adds the overview tab to the beginning of the tabs array
    headerTabsProps.unshift({tabKey: ProcedureRunTab.OVERVIEW, title: 'OVERVIEW'});
  }
  if (selectedProcedureRunPlanId != null || selectedProcedureRunId != null) {
    return {
      headerTabContainerProps,
      headerTabsProps,
    };
  }
  return {};
}

export const MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunOnlyOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRunPlan(id: $id) {
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  ... on ProcedureNode {
                    id
                    name
                  }
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            procedure {
              ... on ProcedureNode {
                id
                name
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function MultiProcedureRunOverviewTabContents(
  props: StudyOverviewTabContentsProps
): JSX.Element {
  assert(
    BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN,
    'Expected STUDIES_ENABLE_PROCEDURE_RUN_DESIGN to be true'
  );
  const procedureRunResult = useSkippableBenchlingQuery<
    MultiProcedureRunOnlyOverviewQuery,
    MultiProcedureRunOnlyOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY, {
    variables: {id: props.runId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: props.runId == null,
  });
  const procedureRunPlanResult = useSkippableBenchlingQuery<
    MultiProcedureRunPlanOverviewQuery,
    MultiProcedureRunPlanOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY, {
    variables: {id: props.runPlanId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: props.runPlanId == null,
  });
  let fetchedProcedureRun = null;
  let fetchedProcedureRunPlan = null;
  // if a user navigates to the procedure run/plan view via Url, we only show the procedure run/plan view if
  // the run/plan ID is valid and if the user has permission to view the run/plan. Otherwise, we navigate to
  // the procedure run/plan listing view
  if (
    procedureRunResult.type === QueryResultType.SUCCESS &&
    procedureRunResult.data &&
    !isInaccessible(procedureRunResult.data.procedureRun)
  ) {
    fetchedProcedureRun = procedureRunResult.data.procedureRun;
  }
  if (
    procedureRunPlanResult.type === QueryResultType.SUCCESS &&
    procedureRunPlanResult.data &&
    !isInaccessible(procedureRunPlanResult.data.procedureRunPlan)
  ) {
    fetchedProcedureRunPlan = procedureRunPlanResult.data.procedureRunPlan;
  }
  const selectedProcedureRun = useMemo(() => {
    if (fetchedProcedureRun != null) {
      return {id: fetchedProcedureRun.id, name: fetchedProcedureRun.name};
    }
    return {id: null, name: null};
  }, [fetchedProcedureRun]);

  const selectedProcedureRunPlan = useMemo(() => {
    if (fetchedProcedureRunPlan != null) {
      const name = getProcedureRunPlanName(fetchedProcedureRunPlan);
      return {id: fetchedProcedureRunPlan.id, name};
    }
    return {id: null, name: null};
  }, [fetchedProcedureRunPlan]);
  assert(
    selectedProcedureRun || selectedProcedureRunPlan,
    'Expected either a procedure run or procedure run plan to be selected.'
  );

  // Use useStateIfMounted to because this can get called from the async callback when conditions are added
  // and the user clicks the "open" button in the success toast.
  const [currentProcedureRunTab, setCurrentProcedureRunTab] = useStateIfMounted<ProcedureRunTab>(
    // When the user navigates to a procedure run from the study overview, we want to show the overview tab
    // unless they are viewing a procedure run plan, in which case we show the design tab
    props.runPlanId != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW
  );

  const studyOverviewContextValue = useMemo(
    () => ({
      selectedProcedureRun,
      selectedProcedureRunPlan,
      currentProcedureRunTab,
      setCurrentProcedureRunTab,
    }),
    [currentProcedureRunTab, selectedProcedureRun, selectedProcedureRunPlan, setCurrentProcedureRunTab]
  );

  if (
    procedureRunResult.type === QueryResultType.PENDING ||
    procedureRunPlanResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  }
  return (
    <StudyOverviewContext.Provider value={studyOverviewContextValue}>
      <StudyExecutionHeader
        {...props}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'overview'}}
        executionContainerStyling={executionContainer}
        headerSectionContainerStyling={headerSectionContainerStyling}
        title={(selectedProcedureRun.name || selectedProcedureRunPlan.name) ?? undefined}
        headerBreadcrumb={studyOverviewCrumbs({
          selectedProcedureRun,
          selectedProcedureRunPlan,
          studyName: props.study.name,
          setCurrentProcedureRunTab,
          studyId: props.study.id,
        })}
        procedureRunPlanId={selectedProcedureRunPlan.id ?? undefined}
        procedureRunId={selectedProcedureRun.id ?? undefined}
        headerButtons={{
          buttons:
            selectedProcedureRun.id != null ? (
              <>
                <LinkToExistingUnitOperationsButton
                  studyId={props.study.id}
                  procedureRunId={selectedProcedureRun.id}
                  procedureRunName={selectedProcedureRun.name}
                />
                <AnalyzeProcedureRunButton
                  studyId={props.study.id}
                  studyName={props.study.name}
                  studyHumanId={props.study.humanId}
                  studyType={props.study.studySchema.studyType}
                  procedureRunId={selectedProcedureRun.id}
                  procedureRunName={selectedProcedureRun.name}
                  procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                  actionOrigin={ActionOrigin.STUDY_RUN_DETAILS_PAGE}
                />
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MEDIUM}
                  isDropdown={false}
                  onClick={() =>
                    copyToClipboard(pathToURL(`${props.study.editUrl}?runId=${selectedProcedureRun.id}`))
                  }
                  icon="link"
                />
                <Dropdown
                  menu={
                    <Menu height={MenuHeight.SMALL} width={MenuWidth.SMALL} dropLeft={true}>
                      <Option
                        onSelect={() => {
                          exportAuditLogs([selectedProcedureRun.id]);
                        }}
                      >
                        <BenchlingIcon icon="audit-trail" />
                        {` ${EXPORT_AUDIT_LOG_DISPLAY_STRING}`}
                      </Option>
                    </Menu>
                  }
                >
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    icon="overflow"
                    size={ButtonSize.MEDIUM}
                    isDropdown={true}
                    hideDropdownCaret={true}
                  />
                </Dropdown>
              </>
            ) : (
              <>
                <AnalyzeMultipleProcedureRunsButton
                  studyId={props.study.id}
                  studyName={props.study.name}
                  studyHumanId={props.study.humanId}
                  studyType={props.study.studySchema.studyType}
                  procedureRunIds={accessibleObjs(props.study.procedureRuns).map(
                    (procedureRun) => procedureRun.id
                  )}
                  procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                  actionOrigin={ActionOrigin.STUDY_OVERVIEW_PAGE}
                />
                <SetStudyPhaseDropdown
                  study={{
                    id: props.study.id,
                    phase: props.study.phase,
                    studyType: props.study.studySchema.studyType,
                  }}
                  refetch={props.refetch}
                  isDisabled={!props.isEditable}
                  requiresApproval={props.isDesignApprovalRequiredToProceed}
                  buttonSize={ButtonSize.MEDIUM}
                  allowReturnToDesignPhase={props.allowReturnToDesignPhase}
                />
                <AddProcedureRunButton
                  studyId={props.study.id}
                  studyType={props.study.studySchema.studyType}
                />
              </>
            ),
          buttonsStyling: buttonStyling,
        }}
        {...procedureRunViewerTabsProps({
          currentProcedureRunTab,
          setCurrentProcedureRunTab,
          selectedProcedureRunId: selectedProcedureRun?.id ?? null,
          selectedProcedureRunPlanId: selectedProcedureRunPlan?.id ?? null,
        })}
      />
    </StudyOverviewContext.Provider>
  );
}

const styles = StyleSheet.create({
  breadcrumbsFont: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
  },
  headerDivider: {
    borderBottom: `1.5px solid ${Colors.GRAY_LIGHT}`,
  },
  tab: {
    ...FONT_PROXIMA_12,
  },
  tabContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.GRAY_DARKER,
    marginTop: 18,
  },
  tabList: {
    marginLeft: '12px',
  },
  tabPanel: {
    marginTop: 0,
  },
});

const executionContainer: React.CSSProperties = {
  flex: '1 0',
};

const buttonStyling: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  marginTop: -35,
};

const headerSectionContainerStyling: React.CSSProperties = {
  margin: '0 12px 12px',
};
